import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { Button, Icon, IconButton, Skeleton, useDisclosure } from '@chakra-ui/react';
import { Box, Flex, Text, Stack } from '@chakra-ui/layout';
import { MdAdd, MdArrowBack } from 'react-icons/md';
import SharedExperienceIcon from '../../../components/icons/sharedExperienceIcon';
import { supabase } from '../../../supabase.client';
import { ProductContext } from '../../lib/productContext';
import { ProductPageContext } from '../lib/productPageContext';
import { useNavigate } from 'react-router-dom';
import SharedExperienceForm from '../../../shared-experiences/components/sharedExperienceForm';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay
} from '@chakra-ui/modal';
import AddSharedExperienceToProductModalForm from './AddSharedExperienceToProductModalForm';
import { useSharedExperiencesApi } from '../../../shared-experiences/lib/useSharedExperiencesApi';
import useTranslation from '../../../lib/i18n/useTranslation';
export default function SharedExperiencesInProductList({ productId }) {
  const { t } = useTranslation();
  const {
    rights,
  } = useContext(ProductContext);
  const {
    isFetchingSharedExperiences,
    sharedExperienceList,
    refreshSharedExperienceList,
    refreshExperienceList,
    addingSharedExperience,
    setAddingSharedExperience,
  } = useContext(ProductPageContext);
  const [isAdding, setIsAdding] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const modalAddSharedExperience = useDisclosure();
  const modalCreateSharedExperience = useDisclosure();
  const ref = useRef(null);
  const ref1 = useRef(null);
  const navigate = useNavigate();
  const sharedExperiencesApi = useSharedExperiencesApi();

  useEffect(() => {
    if (addingSharedExperience) {
      modalAddSharedExperience.onOpen();
    }
  }, [addingSharedExperience]);

  const handleCloseAddModal = () => {
    modalAddSharedExperience.onClose();
    setAddingSharedExperience(false);
  };

  const removeOneSharedExperienceFromProduct = async (sharedExperienceId, productId) => {
    try {
      const { error } = await supabase.rpc('remove_shared_experience_from_product', {
        arg_product_id: productId,
        arg_shared_experiences_id: sharedExperienceId
      });
      
      if (error) {
        console.error('Error removing shared experience from product:', error);
        throw error;
      }
      
      await refreshSharedExperienceList();
      await refreshExperienceList();
      
    } catch (error) {
      console.error('Error removing shared experience from product:', error);
    }
  };
  return (
    <>
      <Flex 
        w="100%"
        flexDirection={"column"}
        py={3}
      >
        <Flex 
          alignItems='center'
          justifyContent={"space-between"}
          px={3}
          mb={2}
        >
          <Flex
            alignItems='center'
          >
            <Icon
              as={SharedExperienceIcon}
              boxSize={'14px'}
              mr={2}
            />
            <Text fontSize={14}>{t('product.sharedExperiences')}</Text>
          </Flex>
          {rights?.can_update && (
            <IconButton
              ml={2}
              variant="purpleOutline"
              icon={<MdAdd />}
              size="sm"
              onClick={modalAddSharedExperience.onOpen}
            />
          )}
        </Flex>
        <Box maxH={300} overflowY={'auto'}>
          {isFetchingSharedExperiences && (
            <Stack p={3} spacing={2} backgroundColor={'white'}>
              <Skeleton height='30px' />
              <Skeleton height='30px' />
              <Skeleton height='30px' />
            </Stack>
          )}
          {(!isFetchingSharedExperiences && (!sharedExperienceList || sharedExperienceList.length === 0)) && (
            <Box px={3}>
              <Text color="text.light.grey">{t('product.noSharedExperiences')}</Text>
            </Box>
          )}
          {(!isFetchingSharedExperiences && sharedExperienceList && sharedExperienceList.length > 0) && (
            <Stack spacing={0}>
              {sharedExperienceList
                .filter((link, index, self) => 
                  self.findIndex(l => l.shared_experiences?.id === link.shared_experiences?.id) === index
                )
                .map((link) => (
                <Flex
                  key={link.id}
                  px={3}
                  h={"34px"}
                  backgroundColor="white"
                  role={'group'}
                  _hover={{
                    background: "background.grey",
                    borderRadius: '2px'
                  }}
                  position={"relative"}
                  alignItems="center"
                  justifyContent="space-between"
                  mb={0}
                  cursor="pointer"
                  onClick={() => {
                    navigate(`/shared-experiences/${link.shared_experiences?.id}`);
                  }}
                >
                  <Flex alignItems="center" flex={1}>
                    <Text fontSize="sm" noOfLines={1}>
                      {link.shared_experiences?.name}
                    </Text>
                  </Flex>
                  {rights?.can_update && (
                    <Box style={{ opacity: '0' }} _groupHover={{ opacity: '1 !important' }} >
                      <IconButton
                        icon={<Icon as={MdAdd} transform="rotate(45deg)" />}
                        size={'md'}
                        variant='greyTransparent'
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          removeOneSharedExperienceFromProduct(link.shared_experiences?.id, productId);
                        }}
                      />
                    </Box>
                  )}
                </Flex>
              ))}
            </Stack>
          )}
        </Box>
      </Flex>
      <Modal isOpen={modalAddSharedExperience.isOpen} onClose={handleCloseAddModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('product.addSharedExperience')}</ModalHeader>
          <ModalBody>
            <Box p={3}>
              <AddSharedExperienceToProductModalForm
                productId={productId}
                hideSave={true}
                onSubmitting={() => {
                  setIsAdding(true);
                }}
                onSubmitted={async () => {
                  setIsAdding(false);
                  handleCloseAddModal();
                  await refreshSharedExperienceList();
                  await refreshExperienceList();
                }}
                onCreateNew={async (name) => {
                  modalAddSharedExperience.onClose();
                  modalCreateSharedExperience.onOpen();
                }}
                onValidityChange={setIsValid}
                ref={ref}
              />
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='purpleOutline'
              onClick={handleCloseAddModal}
            >
              Cancel
            </Button>
            <Button
              variant="purpleFill"
              ml={2}
              isLoading={isAdding}
              isDisabled={!isValid}
              onClick={() => {
                ref.current.click();
              }}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={modalCreateSharedExperience.isOpen} onClose={modalCreateSharedExperience.onClose}>
        <ModalOverlay />
        <ModalContent >
          <ModalHeader>
            <IconButton
              fontSize='24'
              variant="tertiary"
              icon={<MdArrowBack />}
              aria-label='Back'
              mr={2}
              onClick={() => {
                modalCreateSharedExperience.onClose();
                modalAddSharedExperience.onOpen();
              }}
            />
            {t('sharedExperience.newSharedExperiences')}</ModalHeader>
          <ModalBody >
            <Box p={3}>
              <SharedExperienceForm
                hideSave={true}
                onSubmitting={() => {
                  setIsAdding(true);
                }}
                onSubmitted={async (newSharedExperience) => {
                  setIsAdding(false);
                  if (newSharedExperience && newSharedExperience.id) {
                    try {
                      await sharedExperiencesApi.linkProductToSharedExperience(newSharedExperience.id, productId);
                      await refreshSharedExperienceList();
                      await refreshExperienceList();
                    } catch (error) {
                      console.error('Error linking new shared experience:', error);
                    }
                    modalCreateSharedExperience.onClose();
                  }
                }}
                onValidityChange={setIsValid}
                ref={ref1}
              />
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='purpleOutline'
              onClick={() => {
                modalCreateSharedExperience.onClose();
                modalAddSharedExperience.onOpen();
              }}
            >{t('common.cancel')}</Button>
            <Button
              variant="purpleFill"
              ml={2}
              isLoading={isAdding}
              isDisabled={!isValid}
              onClick={() => {
                ref1.current.click();
              }}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
} 